@if (loading()) {
  <div class="w-full animate-pulse">
    @if (showTitle()) {
      <div class="w-full flex justify-center items-center text-center my-2">
        <div class="h-7 bg-gray-300 rounded w-48"></div>
      </div>
    }
    <div
      class="w-full"
      [class]="
        legendPosition() === 'right'
          ? 'md:flex flex-row'
          : legendPosition() === 'left'
            ? 'md:flex flex-row-reverse'
            : ''
      "
    >
      <!-- Chart loading placeholder -->
      <div
        class="flex justify-center items-center"
        [class]="legendPosition() !== 'bottom' ? 'md:w-1/2' : 'w-full'"
      >
        <div
          [class]="chartType() === 'gauge' ? 'aspect-[2/1]' : 'aspect-square'"
          [class]="size()"
        >
          <div
            class="w-full h-full bg-gray-200 flex items-center justify-center"
            [class]="chartType() === 'gauge' ? 'rounded-t-full' : 'rounded-full'"
          >
            <div
              class="w-3/4 h-3/4 bg-gray-300 flex items-center justify-center"
              [class]="chartType() === 'gauge' ? 'rounded-t-full' : 'rounded-full'"
            >
              <div
                [class]="
                  chartType() === 'gauge'
                    ? 'w-1/2 h-1/2 bg-surface rounded-t-full'
                    : 'w-1/2 h-1/2 bg-surface rounded-full'
                "
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Legend loading placeholder -->
      @if (withLegend()) {
        <div
          class="flex justify-center items-center mt-4"
          [class]="legendPosition() !== 'bottom' ? 'md:w-1/2 md:mt-0' : 'w-full'"
        >
          <ul class="flex flex-col gap-1 text-sm w-full">
            @for (item of [1, 2, 3, 4]; track item) {
              <li class="flex gap-2 items-center p-1 w-full">
                <div class="w-4 h-4 bg-gray-300 rounded flex-shrink-0"></div>
                <div class="flex justify-between w-full">
                  <div
                    class="h-4 bg-gray-300 rounded"
                    [style.width]="
                      item === 1 ? '40%' : item === 2 ? '55%' : item === 3 ? '45%' : '60%'
                    "
                  ></div>
                  <div class="h-4 bg-gray-300 rounded w-16"></div>
                </div>
              </li>
            }
            <li class="flex p-1 items-center w-full border-t pt-2">
              <div class="flex justify-between w-full">
                <div class="h-4 bg-gray-300 rounded w-16"></div>
                <div class="h-4 bg-gray-300 rounded w-20"></div>
              </div>
            </li>
          </ul>
        </div>
      }
    </div>
  </div>
} @else {
  @if (showTitle()) {
    <h2
      class="font-bold w-full flex justify-center items-center text-center mt-2 text-lg mb-4"
    >
      {{ title() }}
    </h2>
  }
  <div
    class="w-full"
    [class]="
      legendPosition() === 'right'
        ? 'md:flex flex-row'
        : legendPosition() === 'left'
          ? 'md:flex flex-row-reverse'
          : ''
    "
  >
    <div
      class="flex justify-center items-center"
      [class]="legendPosition() !== 'bottom' ? 'md:w-1/2' : 'w-full'"
    >
      <div
        [class]="chartType() === 'gauge' ? 'aspect-[2/1]' : 'aspect-square'"
        [class]="size()"
      >
        <svg
          [attr.viewBox]="chartType() === 'gauge' ? '0 0 120 80' : '0 0 120 120'"
          class="w-full h-full"
        >
          @if (chartType() === 'donut') {
            <!-- Donut Chart -->
            <g transform="rotate(-90 60 60)">
              @for (segment of animatedSvgSegments(); track segment.id) {
                <circle
                  r="50"
                  cx="60"
                  cy="60"
                  [attr.stroke]="segment.color"
                  [attr.stroke-dasharray]="segment.strokeDasharray"
                  [attr.stroke-dashoffset]="segment.strokeDashoffset"
                  [attr.stroke-width]="20"
                  (mouseover)="
                    withLegend() ? highlightedSegment.set(segment.label) : null
                  "
                  (mouseout)="withLegend() ? highlightedSegment.set(null) : null"
                  fill="none"
                  class="transition-opacity duration-200"
                  (click)="onSegmentClick()?.(segment.label)"
                  [class]="
                    highlightedSegment() &&
                    (highlightedSegment() !== segment.label ? 'opacity-20' : '') +
                      ' ' +
                      (withLegend() ? ' cursor-pointer' : ' cursor-default')
                  "
                ></circle>
              }
            </g>
          } @else {
            <!-- Gauge Chart -->
            <!-- Background track -->
            <path [attr.d]="gaugePath()" stroke="#e5e7eb" stroke-width="20" fill="none" />

            <!-- Gauge segments -->
            @for (segment of animatedSvgSegments(); track segment.id) {
              <path
                [attr.d]="gaugePath()"
                [attr.stroke]="segment.color"
                [attr.stroke-dasharray]="segment.strokeDasharray"
                [attr.stroke-dashoffset]="segment.strokeDashoffset"
                [attr.stroke-width]="20"
                (mouseover)="withLegend() ? highlightedSegment.set(segment.label) : null"
                (mouseout)="withLegend() ? highlightedSegment.set(null) : null"
                fill="none"
                class="transition-opacity duration-200"
                (click)="onSegmentClick()?.(segment.label)"
                [class]="
                  highlightedSegment() &&
                  (highlightedSegment() !== segment.label ? 'opacity-20' : '') +
                    ' ' +
                    (withLegend() ? ' cursor-pointer' : ' cursor-default')
                "
              />
            }
          }

          <!-- Center text (works for both types) -->
          @if (showTotal() && totalSuffix() !== '') {
            <text
              x="60"
              [attr.y]="chartType() === 'gauge' ? '65' : '60'"
              text-anchor="middle"
              class="font-semibold fill-color"
              style="font-size: 12px"
            >
              {{ total() | number: this.numberFormat() }}
            </text>
            <text
              x="60"
              [attr.y]="chartType() === 'gauge' ? '75' : '75'"
              text-anchor="middle"
              class="fill-color-400"
              style="font-size: 8px"
            >
              {{ this.totalSuffix() }}
            </text>
          }
          @if (showTotal() && totalSuffix() === '') {
            <text
              x="60"
              [attr.y]="chartType() === 'gauge' ? '68' : '68'"
              text-anchor="middle"
              class="font-semibold fill-color"
              [style.font-size]="totalSize()"
            >
              {{ total() | number: this.numberFormat() }}
            </text>
          }
          @if (showPercentage() && totalSuffix() !== '') {
            <text
              x="60"
              [attr.y]="chartType() === 'gauge' ? '65' : '60'"
              text-anchor="middle"
              class="font-semibold fill-color"
              style="font-size: 14px"
            >
              {{ percentage() | number: this.numberFormat() }}%
            </text>
            <text
              x="60"
              [attr.y]="chartType() === 'gauge' ? '75' : '75'"
              text-anchor="middle"
              class="fill-color-400"
              style="font-size: 8px"
            >
              {{ this.totalSuffix() }}
            </text>
          }
          @if (showPercentage() && totalSuffix() === '') {
            <text
              x="60"
              [attr.y]="chartType() === 'gauge' ? '68' : '68'"
              text-anchor="middle"
              class="font-semibold fill-color"
              [style.font-size]="totalSize()"
            >
              {{ percentage() | number: this.numberFormat() }}%
            </text>
          }
        </svg>
      </div>
    </div>
    @if (withLegend()) {
      <div
        class="flex justify-center items-center mt-4"
        [class]="legendPosition() !== 'bottom' ? 'md:w-1/2 md:mt-0' : 'w-full'"
      >
        <ul class="flex flex-col gap-1 text-sm w-full">
          @for (segment of segments(); track segment.label) {
            <li
              class="flex gap-2 cursor-pointer items-center text-center hover:bg-hover rounded w-full p-1"
              (mouseover)="highlightedSegment.set(segment.label)"
              (mouseout)="highlightedSegment.set(null)"
              (click)="onSegmentClick()?.(segment.label)"
              [class]="
                highlightedSegment() &&
                (highlightedSegment() !== segment.label ? 'opacity-20' : 'bg-hover')
              "
            >
              <span
                class="w-4 h-4 rounded flex-shrink-0"
                [style.background]="segment.color"
              ></span>
              <span class="flex justify-between w-full">
                <p>{{ segment.label }}</p>
                <p>
                  @if (numberType() === 'number') {
                    {{ segment.value | number: this.numberFormat() }}
                  }
                  @if (numberType() === 'hours') {
                    {{ segment.value | hours }}
                  }
                  {{ this.numberSuffix() }}
                </p>
              </span>
            </li>
          }
          <li class="flex p-1 items-center text-center rounded w-full border-t">
            <span class="flex justify-between w-full font-bold">
              <p>Total</p>
              @if (numberType() === 'number') {
                <p>
                  {{ total() | number: this.numberFormat() }} {{ this.numberSuffix() }}
                </p>
              }
              @if (numberType() === 'hours') {
                <p>{{ total() | hours }} {{ this.numberSuffix() }}</p>
              }
            </span>
          </li>
        </ul>
      </div>
    }
  </div>
}
